// ─── La scheda della prenotazione ───────────────────────────────────────────
//
// 🖐️ **LA SCHEDA SI APRE PIENA O NON SI APRE** (§ 03.3). È un difetto già
// pagato dal gestionale di oggi: quando la lettura del roster falliva, la
// scheda compariva **senza giocatori** e sembrava una partita vuota — cioè
// mentiva, invece di dire che non aveva potuto leggere.
// ⇒ Qui i giocatori arrivano dalla STESSA richiesta della prenotazione: o c'è
//   tutto, o si vede il guasto.
//
// 🔄⭐⭐ **DAL 16/09/2026 NON È PIÙ UN FOGLIO IN MEZZO ALLO SCHERMO: È IL
// PANNELLO A FIANCO** (⇒ `Pannello.tsx`, scelta sua fra tre disegni).
// 🗣️ Da cosa nasce: *«mi si apre prima una scheda e poi una seconda scheda.
//   Possiamo ottimizzare tutto in una sola scheda? Magari riuscendo a far vedere
//   tutte le informazioni nella stessa schermata.»*
//
// 📏 **Le tre schede erano tre cose diverse, misurate a 1440×850 prima di
// toccare niente** — ed è il motivo per cui la cura è in tre punti e non in uno:
// · aprendo una prenotazione il `<dialog>` era **uno solo**, ma nasceva alto
//   **104 px** con scritto «Sto leggendo…» e diventava **748 px**: due schede
//   all'occhio, una sola nel codice ⇒ qui la cornice è **sempre della sua
//   misura** e dentro si riempie;
// · «Incassa» apriva un **secondo foglio vero** sopra il primo (748 + 559 px)
//   ⇒ adesso il modulo si apre **dentro la riga** di chi paga;
// · e il foglio voleva **1080 px di altezza** in una finestra da 850: 334 px
//   restavano sotto il bordo mentre ai lati 832 px non servivano a niente.
//
// 🔄🚨⭐⭐ **E LA SERA DEL 17/09 È CADUTA ANCHE L'ULTIMA: LA SCHEDA È UNA SOLA.**
// 🗣️ Sue parole, con le due schermate una accanto all'altra sotto gli occhi:
//   *«non capisco perché mi hai fatto due schede simili. A me ne basta solo
//   una… non possiamo far sì che ce n'è una sola di scheda dove c'è tutto?»*.
// ⚖️ Il bottone «Incassa» non apriva nessuna finestra — cambiava le colonne
//   della tabella — e **era lo stesso una seconda scheda**: stessa cornice,
//   contenuto diverso, e nessuna delle due mostrava tutto. Per vedere «a
//   carico» bisognava uscire dall'incasso; per incassare, entrarci.
// 📌 *Due schermate non diventano una perché stanno nella stessa cornice: lo
//    diventano quando non si deve più scegliere quale guardare.*
// ⇒ Colonne tutte insieme — spunta · chi · a carico · incassa · mezzo · ✕ —
//   e niente più da aprire o da chiudere.
//
// 🔄⭐ **E IL 17/09/2026 ERANO GIÀ CADUTE LE ALTRE DUE**, su sua richiesta
// (*«io vorrei fare tutto con un clic e riempire la scheda; poi annullo, un
// messaggio di conferma, e si cancella la prenotazione»*):
// · i «Dettagli» sono ora lo **stesso modulo** della prenotazione nuova
//   (⇒ `pezzi-scheda.tsx`) ⇒ premere «Crea» non cambia faccia, accende questa;
// · l'annullo non apre più un `window.prompt()` — una finestra **del browser**,
//   per giunta con il suo «Annulla» accanto a una prenotazione da annullare —
//   ma chiede nel **piede**, dove si è premuto, e a cosa fatta **chiude il
//   pannello**: il blocco non c'è più sul calendario, e non resta niente da
//   chiudere a mano.

import { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AMBITI_SERIE,
  CAPIENZA,
  ETICHETTE_AMBITO,
  ETICHETTE_CADENZA,
  comeSiRipete,
  etichettaMetodo,
  formatCents,
  oraPiuMinuti,
  siPrezzaColListinoLezioni,
  sociGiaInCampo,
  type AmbitoSerie,
  type GiornoSaltato,
  type Maestro,
  type Prenotazione,
  type SchedaPrenotazione,
  vuoleMaestro,
} from '@pmo/shared';
import { CampoData } from '../../components/CampoData.js';
import { Attesa, Campo, Guasto } from '../../components/pezzi.js';
import { MenuMezzo, totaleInEuro, usaIncassoRapido } from '../cassa/Incasso.js';
import { giaPagatoCents, type ChiPaga } from '../cassa/riga-incasso.js';
import { usaStornoIncasso } from '../cassa/dati.js';
import { CaparraDellaScheda } from './CaparraDellaScheda.js';
import { ErroreApi } from '../../lib/api.js';
import { LezioneDellaScheda, PartitaDellaScheda } from './PartitaDellaScheda.js';
import { CambioOrganizzatoreDellaScheda } from './CambioOrganizzatore.js';
import { euro } from '../../routes/impostazioni/comune.js';
import { giornoPerEsteso, giornoSenzaAnno } from '../../lib/time.js';
import { Pannello, TestaPannello } from './Pannello.js';
import {
  AggiungiGiocatore,
  AvvisoMaestroAltrove,
  CampoNote,
  ImportoModificabile,
  ModuloDettagli,
  cosaManca,
  type ValoriDettagli,
} from './pezzi-scheda.js';
import {
  usaAggiuntaGiocatore,
  usaAnnullo,
  usaModificaGiocatore,
  usaModificaPrenotazione,
  usaModificaSerie,
  usaScheda,
  usaUscitaGiocatore,
} from './dati.js';
import { etichettaTipo, nomeDellaPrenotazione } from './Griglia.js';

/** 🔁 Voce 47 — l'avviso del rinnovo del campo fisso, detto alla Segreteria. */
const ETICHETTA_RINNOVO = {
  avvisato: '🔔 avvisato del rinnovo',
  rinnovato: '✅ rinnovato',
  non_rinnova: '✋ non rinnova',
} as const;
const TITOLO_RINNOVO = {
  avvisato: 'Il socio ha ricevuto l’avviso del rinnovo e non ha ancora risposto.',
  rinnovato:
    'Il socio ha rinnovato il campo fisso dal bot: la serie nuova parte dalla settimana dopo l’ultima.',
  non_rinnova: 'Il socio ha risposto «No, grazie»: dopo l’ultima partita questo campo è libero.',
} as const;

/**
 * ⭐ Il «Salva» sta nel PIEDE, che non scorre, ma resta il bottone del modulo
 * dei dettagli: `form=` lo lega al modulo anche stando fuori dal suo albero.
 * ⚖️ Un bottone che si raggiunge solo scorrendo è la metà del difetto di prima —
 * si compilava un campo e non si vedeva dove confermarlo.
 */
const ID_DETTAGLI = 'dettagli-prenotazione';

/** Chi è in campo, ridotto a quello che la cassa ha bisogno di sapere. */
function rigaDi(g: SchedaPrenotazione['players'][number]): ChiPaga {
  return {
    id: g.id,
    memberId: g.memberId,
    displayName: g.displayName,
    amountDueCents: g.amountDueCents,
  };
}

export interface SchedaProps {
  id: string;
  data: string;
  campi: Array<{ id: string; name: string }>;
  maestri: Maestro[];
  /** 🎾 Voce 9, Q3: le prenotazioni del giorno, per dire se il maestro è già altrove. */
  prenotazioni: readonly Prenotazione[];
  puoModificare: boolean;
  /** 💶 `manage_payments`, che è un permesso diverso da «può modificare». */
  puoIncassare: boolean;
  /**
   * 🔁 I giorni che una serie appena creata NON ha potuto prendere.
   * ⚖️ Arrivano da fuori e non si leggono qui dentro, perché non sono un fatto
   * di questa prenotazione: sono la risposta a un gesto: *«ne ho fatte 9 su 12,
   * e queste tre no»*. Il calendario mostra ciò che esiste, mai ciò che non è
   * nato — se non lo dice questa riga non lo dice più nessuno.
   */
  saltate?: GiornoSaltato[];
  chiudi(): void;
}

export function Scheda(props: SchedaProps): React.JSX.Element {
  const { data: p, isPending, error } = usaScheda(props.id);
  const campo = props.campi.find((c) => c.id === p?.courtId);

  return (
    <Pannello etichetta="La prenotazione" chiudi={props.chiudi}>
      {/*
        ⭐ LA CORNICE C'È SUBITO, e non cambia misura quando arriva la risposta:
        è la cura del salto da 104 a 748 px. Il pannello è alto quanto la
        finestra da fermo, quindi ciò che manca sono le PAROLE, non il riquadro.
      */}
      <TestaPannello
        /*
         * 🔄 **IL NOME DI UNA PRENOTAZIONE È IL SUO TIPO**, dal 17/09/2026 (⇒
         * `pezzi-scheda.tsx` per le sue parole). Qui c'era `p.title ??
         * etichettaTipo(p.type)` con una targhetta accanto che ripeteva il tipo
         * quando il titolo c'era: adesso la cosa da dire è **una sola**, e la
         * targhetta sparisce con lei.
         */
        // 🏷️ Voce 10: il corso si chiama col suo nome (⇒ `nomeDellaPrenotazione`).
        titolo={p ? nomeDellaPrenotazione(p) : 'Prenotazione'}
        sotto={
          p
            ? `${campo?.name ?? 'campo sconosciuto'} · ${giornoPerEsteso(p.date)} · ${p.time}–${oraPiuMinuti(p.time, p.durationMinutes)} · ${p.durationMinutes} minuti`
            : 'sto leggendo…'
        }
        chiudi={props.chiudi}
      />
      {isPending && (
        <div className="corpo-pannello">
          <Attesa cosa="la prenotazione" />
        </div>
      )}
      <Guasto errore={error} />
      {/*
        🔑 La chiave sull'identificativo: passando da una partita all'altra senza
        chiudere il pannello — che è la cosa per cui questo pannello esiste — lo
        stato locale (le spunte dell'incasso, il testo che si stava
        scrivendo) deve ripartire da quello della partita nuova, non restare
        addosso alla precedente.
      */}
      {p && <Contenuto key={p.id} {...props} p={p} />}
    </Pannello>
  );
}

function Contenuto({
  p,
  data,
  maestri,
  campi,
  prenotazioni,
  puoModificare,
  puoIncassare,
  saltate = [],
  chiudi,
}: SchedaProps & { p: SchedaPrenotazione }): React.JSX.Element {
  /**
   * 💶 Com'è messa la riga di ciascuno mentre si compila l'incasso. Non ha più
   * un «aperto»: la tabella è sempre quella. ⇒ `Incasso.tsx`, dove sta anche il
   * perché lo stato non abita nella tabella — il totale e il bottone vivono nel
   * **piede**, che è un altro pezzo della scheda.
   */
  const incasso = usaIncassoRapido(p);
  // L'annullo che sta aspettando un sì: la domanda si apre nel piede, dove sta
  // il bottone che l'ha fatta.
  const [chiedeAnnullo, setChiedeAnnullo] = useState(false);
  /**
   * 💶⭐⭐ **CHI STA PER USCIRE DAL CAMPO AVENDO GIÀ PAGATO — 18/09/2026.**
   *
   * 📏 Misurato sull'app viva: si incassano 11,00 € da un giocatore e si preme
   *   la sua croce. Usciva **al primo clic**, senza una parola — e la sua riga
   *   di `booking_players` viene cancellata davvero, non spenta.
   * ⚖️ L'annullo della prenotazione quei soldi li nomina da sempre (*«⚠️ Ma
   *   11,00 € sono già in cassa: quelli restano, e si stornano a parte»*). La
   *   croce faceva la stessa cosa a una persona sola, e taceva: due gesti che
   *   staccano del denaro dalla sua partita, e uno solo lo diceva.
   * 🚨 E per un OSPITE non è rimediabile: il suo unico segno era il posto in
   *   campo, quindi rimettendolo dentro il denaro **non lo ritrova**
   *   (⇒ `riga-incasso.ts`). Per un socio sì — ma chi preme la croce non sta
   *   pensando a questa differenza.
   * ⇒ `null` = nessuna domanda aperta; altrimenti l'id del posto che la aspetta.
   *   Una sola per volta: due domande aperte insieme sono due righe rosse fra
   *   cui scegliere, e si risponde a quella sbagliata.
   */
  const [chiedeUscita, setChiedeUscita] = useState<string | null>(null);
  /**
   * 🔁 **A chi si applica quello che sto per fare**, quando questa prenotazione
   * è di una serie. Parte da «solo questa», e deve partire da lì: il valore
   * predefinito di un gesto che tocca sei mesi di calendario è sempre quello
   * che fa meno danno.
   */
  const [ambito, setAmbito] = useState<AmbitoSerie>('una');
  // 🔁 La fine della periodicità, mentre la si sposta.
  const [fine, setFine] = useState(p.serie?.fino ?? '');
  useEffect(() => setFine(p.serie?.fino ?? ''), [p.serie?.fino]);
  const cambiaSerie = usaModificaSerie(p.serie?.id ?? null);

  /**
   * ⭐⭐ **I «Dettagli» ABITANO QUI, e non più dentro il loro componente.**
   *
   * ⚖️ Sono saliti il 17/09/2026 per una ragione sola, e non è di ordine: il
   * piede deve spegnere «Salva i dettagli» quando manca una cosa obbligatoria
   * (⇒ `cosaManca`), e la cosa obbligatoria dipende dal **tipo che si sta per
   * salvare** — non da quello salvato. Con lo stato chiuso dentro il modulo, il
   * piede avrebbe potuto guardare solo `p.type`, cioè rispondere alla domanda
   * sbagliata: chi passa una partita a «lezione» vedrebbe il bottone acceso
   * finché non preme.
   * 📌 *Un bottone che si spegne dopo il clic non è una regola: è un rifiuto.*
   */
  const [v, setV] = useState<ValoriDettagli>({
    note: p.notes ?? '',
    tipo: p.type,
    maestro: p.instructorId ?? '',
    durata: p.durationMinutes,
  });

  /*
   * ⚠️ I campi ripartono da quello che dice il server: passando da una partita
   * all'altra senza chiudere il pannello — che è la cosa per cui il pannello
   * esiste — quello che si stava scrivendo non deve restare addosso alla
   * partita nuova. Vale anche quando a cambiare la durata è la maniglia sul
   * calendario mentre la scheda è aperta.
   */
  useEffect(() => {
    setV({
      note: p.notes ?? '',
      tipo: p.type,
      maestro: p.instructorId ?? '',
      durata: p.durationMinutes,
    });
  }, [p.id, p.notes, p.type, p.instructorId, p.durationMinutes]);
  const modifica = usaModificaPrenotazione(data, p.id);
  const annulla = usaAnnullo(data, p.id);
  const [perChi, setPerChi] = useState<'socio' | 'circolo'>('socio');
  const aggiungi = usaAggiuntaGiocatore(data, p.id);
  const cambiaImporto = usaModificaGiocatore(data, p.id);
  const esce = usaUscitaGiocatore(data, p.id);

  const pieno = p.players.length >= CAPIENZA[p.type];
  const assenti = p.players.filter((g) => g.assente).length;

  /* 🎓 19c: su lezione e allenamento la cifra a testa dipende da quanti sono, e
     chi ha già pagato resta al prezzo di prima (⇒ `ricalcolaGliImporti`). Qui
     si trova chi è rimasto indietro, per dirlo invece di lasciarlo capire. */
  const aListino = siPrezzaColListinoLezioni(p.type);
  const aTesta = p.prezzo.perPlayerCents;
  function alPrezzoVecchio(g: SchedaPrenotazione['players'][number], pagato: number): boolean {
    return (
      (p.type === 'lezione' || p.type === 'allenamento') &&
      aTesta !== null &&
      pagato > 0 &&
      g.amountDueCents !== aTesta
    );
  }
  const rimastiAlPrezzoVecchio = p.players.filter((g) =>
    alPrezzoVecchio(g, giaPagatoCents(p.cassa, rigaDi(g), p.players)),
  );
  const annullata = p.status === 'cancelled';
  const modificabile = puoModificare && !annullata;
  /*
   * 💶 Se le colonne dell'incasso hanno un senso su questa scheda. Non è un
   * «modo» che si accende e si spegne: è un **permesso** (`manage_payments`) e
   * uno stato della prenotazione. Chi ce l'ha le vede sempre, chi non ce l'ha
   * non le vede mai — e nessuno dei due deve premere niente per cambiare idea.
   */
  const conIncasso = puoIncassare && !annullata && p.players.length > 0;
  // 🔒 Cosa non può mancare, per il tipo che si sta per salvare.
  const manca = cosaManca(v.tipo, p.players.length, v.maestro);

  return (
    <>
      <div className="corpo-pannello">
        {annullata && (
          <p className="riquadro avviso">
            Questa prenotazione è <strong>annullata</strong>
            {p.cancellationReason ? `: ${p.cancellationReason}` : '.'} Resta qui perché la storia non si
            cancella; per giocare se ne fa una nuova.
          </p>
        )}

        {/*
          🔁⭐ **I GIORNI CHE LA SERIE NON HA POTUTO PRENDERE**, detti una volta
            sola e nel posto in cui si è guardando.
          ⚖️ Il calendario mostra ciò che esiste, mai ciò che non è nato: se non
            lo dice questa riga, la segreteria crede di avere il campo tenuto
            per dodici giovedì e ne ha nove. È il prezzo dichiarato del non
            fermare tutta la serie per un giorno occupato — e senza questa riga
            quel prezzo lo pagherebbe qualcuno senza saperlo.
        */}
        {saltate.length > 0 && (
          <p className="avvertenza">
            <strong>
              {saltate.length === 1
                ? 'Un giorno non si è potuto prenotare'
                : `${saltate.length} giorni non si sono potuti prenotare`}
              :
            </strong>{' '}
            {/* ⚠️ Via il punto finale del motivo: dentro una parentesi, dopo la
                quale la frase continua, è un punto in mezzo a una riga. */}
            {saltate.map((g) => `${giornoSenzaAnno(g.date)} (${g.perche.replace(/\.$/, '')})`).join(' · ')}
          </p>
        )}

        <Guasto
          errore={modifica.error ?? annulla.error ?? aggiungi.error ?? cambiaImporto.error ?? esce.error}
        />

        {/* ── che cos'è: si sceglie PRIMA, perché decide il resto ────────── */}
        {modificabile && (
          <>
            <h3 className="titolo-sezione">Che cos’è</h3>
            <ModuloDettagli
              idModulo={ID_DETTAGLI}
              v={v}
              cambia={(c) => setV((x) => ({ ...x, ...c }))}
              maestri={maestri}
              maestroDaTenere={p.instructorId}
              invia={() => {
                if (manca !== null) return;
                /*
                 * ⭐⭐ **SALVATO, LA SCHEDA SI CHIUDE DA SÉ.**
                 * 🗣️ Sua richiesta del 17/09: *«mi piace che cambia colore quando
                 *   cambio tipologia di prenotazione. Però quando clicco su salva,
                 *   si dovrebbe applicare il colore e chiudere la scheda in
                 *   automatico»*.
                 * ⚖️ Il colore sta sul BLOCCO, e il blocco è sotto il pannello:
                 *   salvare e restare aperti vuol dire fare una modifica e non
                 *   vederne l'effetto.
                 * ⛔ E si chiude solo su `onSuccess`, mai comunque: un salvataggio
                 *   rifiutato ha il motivo scritto **dentro** questa scheda, e
                 *   chiudendo lo si porterebbe via proprio quando serve leggerlo.
                 */
                modifica.mutate(
                  {
                    /*
                     * ⚠️ `title` NON si manda, e non è una dimenticanza: mandarlo
                     * a `null` cancellerebbe dal database i titoli vecchi al primo
                     * «Salva», cioè butterebbe via il lavoro di qualcuno di
                     * nascosto. Non si mostrano più da nessuna parte, e restano
                     * dove sono finché lui non dice di toglierli.
                     */
                    notes: v.note.trim() === '' ? null : v.note.trim(),
                    type: v.tipo,
                    instructorId: v.maestro === '' ? null : v.maestro,
                    durationMinutes: v.durata,
                    // 🔁 Su una prenotazione che non è di nessuna serie non
                    //    cambia niente: c'è una prenotazione sola.
                    ambito,
                  },
                  { onSuccess: chiudi },
                );
              }}
            />
            {/* 🎾 Voce 9, Q3 (scelta A): per la segreteria è un avviso, non un blocco. */}
            {vuoleMaestro(v.tipo) && (
              <AvvisoMaestroAltrove
                prenotazioni={prenotazioni}
                maestroId={v.maestro}
                maestri={maestri}
                campi={campi}
                courtId={p.courtId}
                date={p.date}
                time={p.time}
                durata={v.durata}
                escludiId={p.id}
              />
            )}
          </>
        )}

        {/*
          🔁⭐⭐ **LA PERIODICITÀ, SU UNA PRENOTAZIONE CHE C'È GIÀ** — 17/09/2026,
            sua: *«e logicamente di modificare o cancellare una periodicità»*.
          ⚖️ **Si sposta la FINE, nei due versi**: più in là fa nascere le volte
            che mancano, più in qua fa sparire quelle che avanzano. Due bottoni
            — «allunga» e «accorcia» — sarebbero due comandi per una domanda
            sola, e chi guarda dovrebbe sapere prima quale gli serve.
          ⛔ La cadenza non si cambia, ed è dichiarato qui sotto: cambiarla
            vorrebbe dire buttare via metà delle volte già fatte, che possono
            avere gente dentro e soldi in cassa.
        */}
        {p.serie !== null && (
          <>
            <h3 className="titolo-sezione">
              Si ripete
              <span className="tenue piccolo">
                la {p.serie.quale}ª di {p.serie.quante}
              </span>
              {p.serie.dalBot && <span className="dal-bot">🔁 dal bot</span>}
              {/* 🔁 Voce 47: l'avviso del rinnovo al socio, e com'è andato. */}
              {p.serie.rinnovo && (
                <span className="dal-bot" title={TITOLO_RINNOVO[p.serie.rinnovo]}>
                  {ETICHETTA_RINNOVO[p.serie.rinnovo]}
                </span>
              )}
            </h3>
            <p className="tenue piccolo">
              Questa prenotazione si ripete <strong>{ETICHETTE_CADENZA[p.serie.cadenza]}</strong>
              {/*
                ⭐ La cadenza da sola non basta più a descrivere una serie: «ogni
                settimana» non dice se è il giovedì o il lun·mer·ven, e «ogni
                mese» non dice quale giovedì. Chi guarda deve poterlo leggere
                senza contare le prenotazioni sul calendario.
              */}
              {', '}
              {comeSiRipete(p.serie.dal, p.serie)}. Per cambiare il passo, falla finire qui e comincia una
              serie nuova.
            </p>
            {modificabile && (
              <>
                <div className="fine-serie">
                  <CampoData etichetta="Fino al" value={fine} onChange={setFine} />
                  <button
                    type="button"
                    className="piatto"
                    disabled={fine === '' || fine === p.serie.fino || cambiaSerie.isPending}
                    onClick={() => cambiaSerie.mutate({ fino: fine })}
                  >
                    {cambiaSerie.isPending ? 'Un attimo…' : 'Sposta la fine'}
                  </button>
                </div>
                <Guasto errore={cambiaSerie.error} />
                {cambiaSerie.data && (
                  <p className="tenue piccolo">
                    {cambiaSerie.data.nate > 0 && <>Ne sono nate {cambiaSerie.data.nate}. </>}
                    {cambiaSerie.data.tolte > 0 && <>Ne sono state tolte {cambiaSerie.data.tolte}. </>}
                    Adesso sono {cambiaSerie.data.quante}.
                    {cambiaSerie.data.saltate.length > 0 && (
                      <>
                        {' '}
                        ⚠️ Saltati: {cambiaSerie.data.saltate.map((g) => giornoSenzaAnno(g.date)).join(' · ')}
                        .
                      </>
                    )}
                  </p>
                )}
              </>
            )}
          </>
        )}

        {/* ── 🌙 la caparra della fascia serale, se c'è ───────────────────── */}
        <CaparraDellaScheda p={p} puoModificare={modificabile} puoIncassare={puoIncassare} />
        {/* ── 🆕 fase 2: chi manca, il no-show, il lucchetto dell'organizzatore ── */}
        <PartitaDellaScheda p={p} puoIncassare={puoIncassare} />
        {/* ── 🆕 fase 3 (R5): cambia organizzatore ─────────────────────────── */}
        <CambioOrganizzatoreDellaScheda p={p} puoModificare={modificabile} />
        {/* ── 🆕 fase 3 (R6): la lezione della sera e la trattenuta al maestro ── */}
        <LezioneDellaScheda p={p} puoIncassare={puoIncassare} />

        {/* ── chi è in campo ─────────────────────────────────────────────── */}
        <h3 className="titolo-sezione">
          In campo
          <span className="tenue piccolo">
            {p.players.length} di {CAPIENZA[p.type]}
            {/* 🎾 Voce 9, Q1 (27/09/2026, scelta A): chi manca conta, perché paga lo
                stesso (*«mancare non sconta»*). Il numero resta quello dei posti;
                la riga dice quanti di loro non c'erano. */}
            {assenti > 0 && (
              <>
                {' · '}
                <strong>
                  {assenti === 1 ? '1 assente, paga lo stesso' : `${assenti} assenti, pagano lo stesso`}
                </strong>
              </>
            )}{' '}
            · {euro(p.totalDueCents)} a carico · {euro(p.cassa.incassatoCents)} incassati
          </span>
        </h3>

        {/* 🎓 19c: su lezione, allenamento e corso la cifra viene dal «Listino
            prezzi lezioni», e questa riga dice da quale casella — la stessa del
            pannello della prenotazione nuova. */}
        {aListino && (
          <p className={p.prezzo.perPlayerCents === null ? 'da-dove non-deciso' : 'da-dove'}>
            {p.prezzo.perche}
          </p>
        )}
        {/* ⚠️ 19c: chi ha già pagato resta al prezzo di prima quando entra o esce
            qualcuno — decisione sua. Si dice, o la riga diversa dalle altre
            sembra un errore. */}
        {rimastiAlPrezzoVecchio.length > 0 && (
          <p className="avvertenza">
            <strong>
              Adesso è {p.type === 'allenamento' ? 'un' : 'una'}{' '}
              {p.prezzo.perche.split(':')[0]!.toLowerCase()}.
            </strong>{' '}
            {rimastiAlPrezzoVecchio.map((g) => g.displayName).join(', ')}{' '}
            {rimastiAlPrezzoVecchio.length === 1 ? 'ha già pagato' : 'hanno già pagato'}: il suo importo resta
            quello. Se va cambiato, lo riscrivi tu.
          </p>
        )}

        {/* ⭐ Al banco pagano quasi sempre allo stesso modo: il mezzo si mette
            una volta per tutti, e poi si corregge chi fa eccezione. Era la cosa
            che costava quattro scelte identiche di fila. */}
        {conIncasso && (
          <div className="barra-incasso">
            {/*
              ⚠️ «Spunta tutti» sta QUI e non nell'intestazione della tabella,
              dove stava fino a un minuto fa: sul telefono le colonne non ci
              stanno, le righe diventano tre piani e **l'intestazione sparisce**
              — portandosi via quella spunta. In barra si vede a ogni larghezza.
              ⭐ Ed è la scorciatoia che rende veloce il banco: spuntare tutti e
                premere sono **due gesti** per quattro incassi.
            */}
            <label className="campo orizzontale">
              <input
                type="checkbox"
                checked={p.players.every((g) => incasso.scelta(rigaDi(g)).scelto)}
                onChange={(e) => incasso.tutti(e.target.checked)}
              />
              <span className="tenue piccolo">tutti</span>
            </label>
            <span className="tenue piccolo">pagano con</span>
            <MenuMezzo valore="" etichetta="Il mezzo di tutti" cambia={incasso.mezzoPerTutti} />
          </div>
        )}

        {p.players.length === 0 ? (
          <p className="tenue">Non c’è ancora nessuno.</p>
        ) : (
          /*
            ⭐⭐ **UNA TABELLA SOLA, CON DENTRO TUTTO** — 🗣️ *«non possiamo far
            sì che ce n'è una sola di scheda dove c'è tutto?»* (17/09/2026).
            ⚖️ Le colonne non cambiano più a seconda di cosa si sta facendo: chi
              guarda vede quanto uno deve, quanto ha già dato, quanto sta per
              dare e con che mezzo — tutto in fila, senza premere niente.
          */
          <table className="tabella in-campo">
            <thead>
              <tr>
                {conIncasso && <th className="spunta" />}
                <th>chi</th>
                {/* 💶 «A carico» e «incassa» sono due cose diverse e non si sommano
                    mai: la prima è un'intenzione che si riscrive quante volte si
                    vuole, la seconda è cassa. Stanno accanto proprio perché la
                    differenza si veda. */}
                <th>a carico</th>
                {conIncasso && (
                  <>
                    <th>incassa</th>
                    <th>mezzo</th>
                  </>
                )}
                <th className="via" />
              </tr>
            </thead>
            <tbody>
              {p.players.map((g) => {
                const chi = rigaDi(g);
                const pagato = giaPagatoCents(p.cassa, chi, p.players);
                const s = incasso.scelta(chi);
                const borsellino = s.metodo === 'wallet' ? incasso.borsellino(chi) : null;
                return (
                  <Fragment key={g.id}>
                    <tr className={s.scelto ? 'in-incasso' : undefined}>
                      {conIncasso && (
                        <td className="spunta">
                          <input
                            type="checkbox"
                            checked={s.scelto}
                            aria-label={`Incassa da ${g.displayName}`}
                            onChange={(e) => incasso.spunta(chi, e.target.checked)}
                          />
                        </td>
                      )}
                      {/*
                      🗣️ *«se clicco sul nome dentro la scheda della partita mi
                      si apre l'anagrafica»* (17/09/2026). Serve davvero:
                      «quanto ha nel borsellino?» è la domanda che si fa
                      proprio mentre si sta per incassare da lui.
                      ⚠️ L'OSPITE non è un socio e non ha nessuna anagrafica
                      da aprire: là il nome resta scritto e basta, con la sua
                      etichetta — è un nome a mano, non una persona in
                      archivio.
                    */}
                      <td className="chi">
                        {g.memberId === null ? (
                          <>
                            {g.displayName}
                            {/* ⚠️ Attaccata al nome: andando a capo da sola, « ·
                              ospite» sembra una seconda persona in elenco. */}
                            <span className="tenue piccolo etichetta-ospite">&nbsp;· ospite</span>
                          </>
                        ) : (
                          <Link
                            className="nome-in-cassa"
                            to={`/soci?socio=${g.memberId}`}
                            title={`Apri l’anagrafica di ${g.displayName}`}
                          >
                            {g.displayName}
                          </Link>
                        )}
                        {/*
                        ⭐ Sotto il nome ci stanno le due cose che NON meritano una
                        colonna ma non si possono perdere: quanto ha già pagato —
                        scritto solo se ha pagato, o sarebbe una colonna di zeri —
                        e, col borsellino, quello che resterà.
                        ⚠️ Lo scoperto si vede ADESSO, non dopo aver premuto: un
                          incasso che lo scopre dopo è un incasso da stornare.
                      */}
                        <span className="sotto-nome">
                          {/* 🚨⭐⭐ **NON C'ERA, E DEVE LO STESSO.** 🗣️ *«Il corso
                              si paga al mese»* ⇒ si paga il posto, non la
                              presenza. ⚖️ Sta QUI e non solo dall'altra parte
                              perché è di qua che si incassa: senza, chi batte
                              la cassa vede in campo una persona che non c'era e
                              non ha niente che glielo dica.
                              📌 *Un dato che compare solo su una delle due
                                 schermate non è un dettaglio di presentazione:
                                 è una delle due che sbaglia.*
                              ⛔ Di qua si LEGGE: a segnarla è il maestro, che è
                                 l'unico che sa chi c'era. */}
                          {/* ⏱️ Ha avvisato dal bot che arriva in ritardo (27/09/2026):
                              i compagni lo sanno; qui si vede prima di segnarlo assente. */}
                          {g.ritardo && (
                            <span className="in-ritardo piccolo">
                              ⏱️ arriva {g.ritardo.minuti ? `~${g.ritardo.minuti}′ ` : ''}in ritardo (avvisato
                              alle {g.ritardo.at})
                            </span>
                          )}
                          {g.assente && <span className="tenue piccolo">non c’era · paga lo stesso</span>}
                          {pagato > 0 && (
                            <span
                              className={pagato >= g.amountDueCents ? 'saldo-pieno piccolo' : 'tenue piccolo'}
                            >
                              già {euro(pagato)}
                              {alPrezzoVecchio(g, pagato) && ' · il suo importo non cambia'}
                            </span>
                          )}
                          {borsellino !== null && (
                            <span className={borsellino.dopo < 0 ? 'guasto piccolo' : 'tenue piccolo'}>
                              {borsellino.dopo < 0
                                ? `nel borsellino ${euro(borsellino.saldo)}: non bastano`
                                : `borsellino ${euro(borsellino.saldo)} → ${euro(borsellino.dopo)}`}
                            </span>
                          )}
                          {s.metodo === 'wallet' && g.memberId === null && (
                            <span className="guasto piccolo">il borsellino è di un socio</span>
                          )}
                        </span>
                      </td>
                      <td className="carico">
                        {modificabile ? (
                          /*
                          🔗 **CAMBIARE «A CARICO» RIFÀ ANCHE «INCASSA»** —
                          🗣️ *«se io cambio a carico deve anche cambiare
                            incassa»* (18/09/2026).
                          ⚖️ La casella dell'incasso segue già il residuo da sé
                            finché nessuno l'ha battuta a mano; `seguiIlCarico`
                            serve per le righe che INVECE sono state toccate —
                            spuntate, o con un importo scritto sopra — dove
                            altrimenti resterebbe ferma la cifra di prima.
                          📌 *Il numero che parte alla cassa dev'essere lo
                             stesso che si legge nella colonna accanto.*
                        */
                          <ImportoModificabile
                            cents={g.amountDueCents}
                            salva={(cents) => {
                              incasso.seguiIlCarico(chi);
                              cambiaImporto.mutate({ playerId: g.id, amountDueCents: cents });
                            }}
                          />
                        ) : (
                          euro(g.amountDueCents)
                        )}
                      </td>
                      {conIncasso && (
                        <>
                          <td className="quanto">
                            {/* ⭐ Scritto anche a riga spenta, e in grigio: è la risposta a
                              «quanto mi deve ancora?», che è la domanda che si fa PRIMA
                              di spuntare. */}
                            <input
                              className="importo"
                              inputMode="decimal"
                              value={s.importo}
                              disabled={!s.scelto || s.metodo === 'gift'}
                              aria-label={`Quanto incassare da ${g.displayName}`}
                              onChange={(e) => incasso.scriviImporto(chi, e.target.value)}
                            />
                          </td>
                          <td className="mezzo-cella">
                            <MenuMezzo
                              valore={s.metodo}
                              etichetta={`Con che mezzo paga ${g.displayName}`}
                              disabilitato={!s.scelto}
                              crediti={incasso.crediti(chi)}
                              cambia={(m) => incasso.scegliMezzo(chi, m)}
                            />
                          </td>
                        </>
                      )}
                      {/*
                      🩹 **IL «TOGLI» C'È SEMPRE**, ed era sparito mentre si
                      incassava.
                      🗣️ Sua domanda del 17/09/2026: *«ma soprattutto come
                        faccio ad eliminare un giocatore?»*.
                      ⚖️ L'avevo tolto per tenere il gesto pulito — una cosa per
                        volta — ma un gesto che c'è solo se prima ne annulli un
                        altro, per chi guarda non c'è. È la stessa radice delle
                        «due schede»: chiedere a chi usa la scheda di ricordarsi
                        in quale stato l'ha messa.
                      ⭐ Per sola croce, e non per gusto: accanto a cinque
                        colonne la parola «Togli» non ci sta. Il nome per esteso
                        resta nel `title` e nell'`aria-label`.
                    */}
                      <td className="via">
                        {modificabile && (
                          <button
                            type="button"
                            className="piatto pericolo togli-stretto"
                            title={`Togli ${g.displayName} dal campo`}
                            aria-label={`Togli ${g.displayName} dal campo`}
                            /* ⚖️ Chi non ha ancora dato niente esce al primo clic,
                             come sempre: una domanda che si fa ogni volta è una
                             domanda a cui si risponde «sì» senza leggerla, e
                             allora non protegge più nessuno. */
                            onClick={() => (pagato === 0 ? esce.mutate(g.id) : setChiedeUscita(g.id))}
                          >
                            ✕
                          </button>
                        )}
                      </td>
                    </tr>
                    {/*
                    ⚖️ **LA DOMANDA STA SOTTO LA SUA RIGA, LARGA QUANTO LA
                    TABELLA, e al primo tentativo stava dentro la colonna della
                    croce**: là dentro — sette caratteri di larghezza — usciva
                    dal pannello e i due bottoni finivano fuori dallo schermo.
                    ⇒ Una domanda a cui non si può rispondere è peggio di
                      nessuna domanda: blocca il gesto senza offrire la via.
                  */}
                    {chiedeUscita === g.id && (
                      <tr className="domanda-in-riga">
                        <td colSpan={conIncasso ? 6 : 3}>
                          <span className="tenue piccolo">
                            <strong>{g.displayName}</strong> ha già dato <strong>{euro(pagato)}</strong>:
                            quelli restano in cassa e si stornano a parte. Lo tolgo lo stesso?
                          </span>
                          <button
                            type="button"
                            className="pericolo"
                            onClick={() => {
                              setChiedeUscita(null);
                              esce.mutate(g.id);
                            }}
                          >
                            Sì, toglilo
                          </button>
                          <button type="button" className="piatto" onClick={() => setChiedeUscita(null)}>
                            No
                          </button>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}

        {/* ⚖️ Una nota sola per tutto il gruppo: al banco il motivo è uno, e
            quattro caselle da riempire sarebbero quattro caselle da saltare.
            ⚠️ Compare quando c'è qualcuno di spuntato, o sarebbe una seconda
              casella «Note» sempre in vista accanto a quella della
              prenotazione — due caselle uguali per due cose diverse. */}
        {conIncasso && incasso.esame.quante > 0 && (
          <Campo
            etichetta="Nota (finisce su tutte le righe incassate)"
            placeholder="facoltativa"
            value={incasso.nota}
            onChange={(e) => incasso.scriviNota(e.target.value)}
          />
        )}

        {/*
          💶 Non è un incasso, ed è scritto perché la confusione fra i due è il
          modo in cui una cassa smette di quadrare: l'importo a carico è quanto
          una persona DEVE, si riscrive quante volte si vuole, e la sezione
          Incassi non lo vede.
        */}
        {!aListino && (
          <p className="tenue piccolo">
            {p.prezzo.perPlayerCents === null ? (
              <>⚠️ {p.prezzo.perche}</>
            ) : (
              <>
                Il listino propone <strong>{euro(p.prezzo.perPlayerCents)}</strong> a testa (
                {euro(p.prezzo.courtCents ?? 0)} il campo). {p.prezzo.ambigua ? '⚠️ ' : ''}
                {p.prezzo.perche}
              </>
            )}
          </p>
        )}

        {/* ⚖️ Sta QUI e non nel piede accanto a «Salva»: l'importo a carico non lo
            salva quel bottone, e scriverlo lì vorrebbe dire il contrario. */}
        {modificabile && p.players.length > 0 && (
          <p className="tenue piccolo">Gli importi a carico si salvano da soli, appena esci dalla casella.</p>
        )}

        {modificabile && !pieno && (
          <AggiungiGiocatore aggiungi={(g) => aggiungi.mutate(g)} giaInCampo={sociGiaInCampo(p.players)} />
        )}
        {pieno && <p className="tenue">In campo non ci sta nessun altro.</p>}

        {/*
          ── quello che è già passato in cassa ─────────────────────────────
          🗣️ **«Movimenti di cassa»**, parola sua del 18/09/2026 — e non è un
            sinonimo di «In cassa»: qui dentro non ci sono solo incassi, ci
            stanno anche gli **storni**, che dalla cassa tolgono. Un titolo che
            dice «in cassa» sopra una riga da −10,00 € si contraddice da solo.
          ⭐ E l'elenco arriva **dal più recente** (⇒ `incassiDellaPrenotazione`):
            quello che si cerca qui è l'ultima cosa successa.
        */}
        {p.cassa.payments.length > 0 && (
          <>
            <h3 className="titolo-sezione">Movimenti di cassa</h3>
            <table className="tabella incassi-partita">
              <tbody>
                {p.cassa.payments.map((i) => (
                  <RigaCassa key={i.id} i={i} bookingId={p.id} puoIncassare={puoIncassare} />
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* ── le note, in coda: sono la postilla, non una domanda ─────────── */}
        {modificabile && (
          <>
            <h3 className="titolo-sezione">Note</h3>
            <CampoNote idModulo={ID_DETTAGLI} v={v} cambia={(c) => setV((x) => ({ ...x, ...c }))} />
          </>
        )}
      </div>

      {/*
        ⭐ IL PIEDE NON SCORRE. Le due azioni che chiudono un giro di lavoro —
        salvare i dettagli e annullare la partita — stavano in fondo a 1080 px di
        foglio: per premerle bisognava sapere che erano là sotto.
      */}
      {/*
        🚨 Il piede c'è anche per chi NON può modificare la prenotazione: il
        permesso di incassare è un altro (`manage_payments`), e senza questa
        condizione chi ha solo quello vedrebbe le spunte e non il bottone.
      */}
      {(modificabile || conIncasso) && (
        <footer className="piede-pannello">
          {chiedeAnnullo ? (
            /*
             * ⭐⭐ **LA DOMANDA STA QUI DENTRO, E PRIMA ERA UNA FINESTRA DEL
             * BROWSER.** C'era un `window.prompt()`: una schermata di sistema,
             * sopra il pannello, con il pulsante «Annulla» del browser accanto
             * a una prenotazione da annullare — due parole uguali per due gesti
             * opposti.
             * 🗣️ Richiesta sua del 17/09/2026: *«poi annullo, un messaggio di
             *   conferma, e si cancella la prenotazione»* — una domanda sola, e
             *   quando è finita non resta niente da chiudere.
             * ⚖️ Nasce nel piede, attaccata al bottone che l'ha fatta: una
             *   domanda che compare lontano da dove si è premuto sembra un'altra
             *   schermata anche quando non lo è.
             */
            <div className="conferma">
              <p>
                Annullo <strong>{etichettaTipo(p.type)}</strong> delle {p.time}
                {p.serie !== null && ambito !== 'una' && (
                  <>
                    {' '}
                    e <strong>tutte le altre volte che restano</strong>
                    {ambito === 'daQui' ? ' da questa in poi' : ' di questa serie'}
                  </>
                )}
                {/* ⚠️ Il verbo segue il numero: «Sparisce» dopo «e tutte le altre
                    volte» è la frase di un'altra domanda. */}
                {p.serie !== null && ambito !== 'una'
                  ? '? Spariscono dal calendario e i campi tornano liberi.'
                  : '? Sparisce dal calendario e il campo torna libero.'}
                {/*
                  🔄 **NON C'È PIÙ SCRITTO «la scheda resta negli archivi».**
                  🗣️ Sua obiezione del 17/09/2026: *«perché c'è scritto che la
                    scheda resta negli archivi? Se elimino una scheda, perché la
                    devo fare rimanere negli archivi? È solo spazzatura inutile o
                    no?»*.
                  ⇒ Adesso è vero in un caso solo, e il server decide quale: una
                    prenotazione su cui non è passato niente **si cancella per
                    davvero** (⇒ `routes.ts`). L'unica cosa che resta, quando
                    resta, sono i **soldi** — e quelli si dicono, perché sono
                    l'unica cosa che poi bisogna andare a sistemare a mano.
                */}
                {p.cassa.incassatoCents > 0 && (
                  <>
                    {' '}
                    ⚠️ Ma <strong>{euro(p.cassa.incassatoCents)}</strong> sono già in cassa: quelli restano, e
                    si stornano a parte.
                  </>
                )}
              </p>
              {/*
                🔄 **IL «PERCHÉ» NON SI CHIEDE PIÙ, dal 17/09/2026.**
                🗣️ Sue parole, guardando la domanda: *«tutto il campo dove c'è
                  scritto perché è il campo sotto non serve, puoi levarlo»*.
                ⚖️ E aveva ragione due volte: non si riempiva mai — il motivo di
                  un annullo lo sa la segreteria, non serve a chi legge dopo — e
                  quella casella era l'unica cosa in tutta la scheda che
                  rientrava rispetto al resto, perché il riquadro della domanda
                  ha un suo margine. Toglierla raddrizza il piede e leva un
                  passo a un gesto che ne vuole uno solo.
                ⚠️ La colonna `cancellation_reason` resta, e la rotta la accetta
                  ancora: da qui arriva `null`, ma i motivi scritti prima si
                  vedono ancora aprendo una prenotazione annullata.
              */}
              {/*
                🔁⭐ **QUANTE NE VANNO VIA, SI SCEGLIE DENTRO LA DOMANDA** — e non
                  con tre bottoni diversi: la domanda è una, la portata è un
                  parametro. Tre bottoni vorrebbero dire leggerne tre per capire
                  che due non servono.
                ⚠️ «Tutte» vuol dire *tutte quelle che restano*: il passato non
                  si disdice, è già successo.
              */}
              {p.serie !== null && (
                <label className="campo orizzontale">
                  <span className="tenue piccolo">quante</span>
                  <select value={ambito} onChange={(e) => setAmbito(e.target.value as AmbitoSerie)}>
                    {AMBITI_SERIE.map((a) => (
                      <option key={a} value={a}>
                        {a === 'tutte' ? 'tutte quelle che restano' : ETICHETTE_AMBITO[a]}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {annulla.error instanceof ErroreApi && annulla.error.codice === 'CHI_DISDICE_LA_LEZIONE' ? (
                /* 🆕 Fase 3 (R6): la lezione della sera a meno di 48 ore — chi la disdice? */
                <div className="finestra-caparra">
                  <p>{annulla.error.message}</p>
                  <div className="scelte-caparra">
                    <label>
                      <input type="radio" checked={perChi === 'socio'} onChange={() => setPerChi('socio')} />
                      <span>
                        <strong>Il maestro o l’allievo</strong>
                        <br />
                        <span className="tenue piccolo">30 € si tolgono dal compenso del maestro.</span>
                      </span>
                    </label>
                    <label>
                      <input
                        type="radio"
                        checked={perChi === 'circolo'}
                        onChange={() => setPerChi('circolo')}
                      />
                      <span>
                        <strong>Il circolo</strong> · campo, luci, maltempo
                        <br />
                        <span className="tenue piccolo">Niente da togliere.</span>
                      </span>
                    </label>
                  </div>
                </div>
              ) : (
                <Guasto errore={annulla.error} />
              )}
              <div className="azioni">
                <button
                  type="button"
                  className="pericolo"
                  disabled={annulla.isPending}
                  onClick={() =>
                    annulla.mutate(
                      {
                        reason: null,
                        ambito,
                        ...(annulla.error instanceof ErroreApi &&
                        annulla.error.codice === 'CHI_DISDICE_LA_LEZIONE'
                          ? { perChi }
                          : {}),
                      },
                      {
                        /*
                         * ⭐ Il pannello si chiude da sé: il blocco non c'è più sul
                         * calendario (il server rende solo le `confirmed`), e
                         * restare aperti su una scheda annullata era la **seconda
                         * schermata da chiudere** di cui si lamentava.
                         */
                        onSuccess: chiudi,
                      },
                    )
                  }
                >
                  {annulla.isPending
                    ? 'Un attimo…'
                    : p.serie !== null && ambito !== 'una'
                      ? 'Sì, annullale'
                      : 'Sì, annulla la prenotazione'}
                </button>
                <button type="button" className="piatto" onClick={() => setChiedeAnnullo(false)}>
                  No, lascia stare
                </button>
              </div>
            </div>
          ) : (
            <>
              {/*
                ⭐⭐ **IL TOTALE E IL BOTTONE STANNO NEL PIEDE, che non scorre**, e
                  ci stanno SEMPRE — non più solo dentro un «modo incasso».
                ⚖️ In fondo alla tabella si raggiungerebbero scorrendo, e con
                  quattro giocatori, il listino e gli incassi già in cassa la
                  scheda scorre davvero. È la stessa ragione per cui ci stanno
                  «Salva» e «Annulla la prenotazione».
                ⭐ A spunte vuote il bottone c'è ed è spento, con accanto cosa
                  manca: un'azione che compare solo dopo aver fatto la cosa
                  giusta è un'azione che nessuno scopre.
              */}
              {conIncasso && (
                <div className="conferma incasso">
                  {incasso.esame.quante > 0 ? (
                    <p>
                      <strong>{totaleInEuro(incasso.esame)}</strong> da {incasso.esame.quante}{' '}
                      {incasso.esame.quante === 1 ? 'persona' : 'persone'}.
                    </p>
                  ) : (
                    <p className="tenue">Spunta chi paga.</p>
                  )}
                  {/* ⭐ Il motivo per cui il bottone è spento sta ACCANTO al bottone:
                      spento e muto è il modo più diretto per far credere che la
                      schermata sia rotta. ⚠️ Ma non quando il motivo è solo «non hai
                      ancora spuntato nessuno»: lì sta già scritto qui sopra, e
                      ripeterlo in giallo sembrerebbe un guasto. */}
                  {incasso.esame.quante > 0 && incasso.esame.manca !== null && (
                    <p className="motivo">{incasso.esame.manca}</p>
                  )}
                  {/*
                    ⚠️ **«Era già registrato» NON è un guasto e non è un «fatto»:
                    è la terza risposta**, e va detta con parole sue. Il server
                    la manda da sempre (`creato: false`) e questa schermata la
                    buttava via ⇒ una riga non scritta aveva lo stesso identico
                    aspetto di una scritta, e il denaro mancante non lo diceva
                    nessuno.
                  */}
                  {incasso.giaRegistrato !== null && <p className="motivo">⚠️ {incasso.giaRegistrato}</p>}
                  <Guasto errore={incasso.errore} />
                  <div className="azioni">
                    <button
                      type="button"
                      disabled={incasso.esame.manca !== null || incasso.inCorso}
                      onClick={incasso.invia}
                    >
                      {incasso.inCorso
                        ? 'Un attimo…'
                        : incasso.esame.quante > 0
                          ? `Incassa ${totaleInEuro(incasso.esame)}`
                          : 'Incassa'}
                    </button>
                    {incasso.esame.quante > 0 && (
                      <button type="button" className="piatto" onClick={incasso.azzera}>
                        Lascia stare
                      </button>
                    )}
                  </div>
                </div>
              )}
              {modificabile && (
                <div className="riga-piede">
                  {/* ⭐ Il motivo per cui «Salva» è spento sta ACCANTO a «Salva»: un
                    bottone spento senza il perché a fianco è il modo più diretto
                    per far credere che la schermata sia rotta. */}
                  {manca !== null && <p className="motivo">{manca}</p>}
                  <button type="button" className="piatto pericolo" onClick={() => setChiedeAnnullo(true)}>
                    Annulla la prenotazione
                  </button>
                  <span className="riempi" />
                  {/*
                    🔁⭐ **A CHI SI APPLICA IL SALVATAGGIO**, e sta ACCANTO a
                      «Salva» perché è la stessa decisione: premere senza aver
                      visto questa casella vuol dire toccare una prenotazione
                      sola, che è quello che di solito si vuole.
                    ⚠️ Il **giorno** resta di questa e basta — una serie è fatta
                      di giorni diversi, e dargliene uno solo la farebbe cadere
                      tutta sullo stesso. Cambiano l'ora, il campo, la durata,
                      il tipo, il maestro e le note.
                  */}
                  {p.serie !== null && (
                    <label className="campo orizzontale">
                      <span className="tenue piccolo">applica a</span>
                      <select value={ambito} onChange={(e) => setAmbito(e.target.value as AmbitoSerie)}>
                        {AMBITI_SERIE.map((a) => (
                          <option key={a} value={a}>
                            {ETICHETTE_AMBITO[a]}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  <button type="submit" form={ID_DETTAGLI} disabled={modifica.isPending || manca !== null}>
                    {modifica.isPending ? 'Un attimo…' : 'Salva i dettagli'}
                  </button>
                </div>
              )}
            </>
          )}
        </footer>
      )}
    </>
  );
}

/**
 * Una riga di cassa dentro la scheda, con il suo storno.
 *
 * ⛔ Lo storno lascia in piedi DUE righe (§ 01.6): questa e quella che la
 * annulla. Qui si vedono tutt'e due, ed è voluto — una cassa in cui le
 * correzioni spariscono è una cassa che non si può controllare.
 */
function RigaCassa({
  i,
  bookingId,
  puoIncassare,
}: {
  i: SchedaPrenotazione['cassa']['payments'][number];
  bookingId: string;
  puoIncassare: boolean;
}): React.JSX.Element {
  const storna = usaStornoIncasso(bookingId);
  const [conferma, setConferma] = useState(false);
  const eUnoStorno = i.voidsPaymentId !== null;

  return (
    <tr className={eUnoStorno ? 'storno' : i.status === 'voided' ? 'stornato' : ''}>
      <td className="tenue piccolo">{new Date(i.recordedAt).toLocaleTimeString('it-IT').slice(0, 5)}</td>
      <td>{i.playerName ?? '—'}</td>
      <td>{etichettaMetodo(i.method)}</td>
      <td className={i.amountCents < 0 ? 'saldo-rosso' : ''}>{formatCents(i.amountCents)}</td>
      <td>
        {puoIncassare && !eUnoStorno && !i.depositId && i.status === 'paid' && !conferma && (
          <button type="button" className="piatto pericolo" onClick={() => setConferma(true)}>
            Storna
          </button>
        )}
        {i.status === 'voided' && <span className="tenue piccolo">stornato</span>}
        {eUnoStorno && <span className="tenue piccolo">storno</span>}
        {conferma && (
          <div className="conferma">
            <p>
              Stornare <strong>{formatCents(i.amountCents)}</strong> di {i.playerName ?? 'questa riga'}?
              Resteranno due righe.
            </p>
            <Guasto errore={storna.error} />
            <div className="azioni">
              <button
                type="button"
                className="pericolo"
                disabled={storna.isPending}
                onClick={() =>
                  storna.mutate({ id: i.id, reason: null }, { onSuccess: () => setConferma(false) })
                }
              >
                {storna.isPending ? 'Un attimo…' : 'Sì, storna'}
              </button>
              <button type="button" className="piatto" onClick={() => setConferma(false)}>
                No
              </button>
            </div>
          </div>
        )}
      </td>
    </tr>
  );
}
